import React from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { LayoutDashboard, FileText, History, LogOut, Gift, Activity, User, DollarSign, MessageSquare } from 'lucide-react';
import { logout, getClinic } from '../utils/auth';
import { toast } from 'sonner';

const DEFAULT_LOGO_URL = '/logo-holons.svg';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const clinic = getClinic();
  
  // Use clinic's custom logo if available, otherwise use default
  const logoUrl = clinic?.clinic_logo_url || DEFAULT_LOGO_URL;

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Sidebar */}
      <div className="fixed left-0 top-0 h-full w-64 hidden md:flex flex-col border-r bg-white">
        <Link to="/dashboard" className="p-6 pb-4 border-b bg-gradient-to-b from-[hsl(190_60%_98%)] to-white cursor-pointer hover:opacity-90 transition-opacity">
          <div className="flex items-center justify-center mb-1">
            <img 
              src={logoUrl} 
              alt="Clinic Logo" 
              className="w-72 h-auto max-h-44 object-contain"
              style={{ mixBlendMode: 'multiply' }}
              onError={(e) => {
                e.target.src = DEFAULT_LOGO_URL;
              }}
            />
          </div>
          <div className="text-center">
            <p className="text-xs font-light tracking-wider text-gray-600 uppercase" style={{fontFamily: 'Chivo, sans-serif'}}>Veterinary</p>
            <div className="h-px bg-gradient-to-r from-transparent via-primary to-transparent my-1 mx-auto w-24"></div>
            <p className="text-xs font-medium text-primary tracking-wider" style={{fontFamily: 'Karla, sans-serif'}}>CLIENT PORTAL</p>
          </div>
        </Link>

        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          <div className="mb-4">
            <p className="text-xs font-semibold text-muted-foreground px-3 mb-3 tracking-wider">OVERVIEW</p>
            <Link to="/dashboard">
              <Button 
                variant={isActive('/dashboard') ? 'secondary' : 'ghost'} 
                className={`w-full justify-start transition-all duration-200 ${
                  isActive('/dashboard') 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                    : 'hover:bg-muted hover:translate-x-1'
                }`}
                data-testid="nav-dashboard"
              >
                <LayoutDashboard className="mr-3" size={20} />
                Dashboard
              </Button>
            </Link>
          </div>

          <div className="mb-4">
            <p className="text-xs font-semibold text-muted-foreground px-3 mb-3 tracking-wider">COLLECTION REQUEST</p>
            <Link to="/requests/new">
              <Button 
                variant={isActive('/requests/new') ? 'secondary' : 'ghost'} 
                className={`w-full justify-start transition-all duration-200 ${
                  isActive('/requests/new') 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                    : 'hover:bg-muted hover:translate-x-1'
                }`}
                data-testid="nav-new-request"
              >
                <FileText className="mr-3" size={20} />
                New Pet Collection
              </Button>
            </Link>
            <Link to="/requests/history">
              <Button 
                variant={isActive('/requests/history') ? 'secondary' : 'ghost'} 
                className={`w-full justify-start transition-all duration-200 ${
                  isActive('/requests/history') 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                    : 'hover:bg-muted hover:translate-x-1'
                }`}
                data-testid="nav-history"
              >
                <History className="mr-3" size={20} />
                History
              </Button>
            </Link>
          </div>

          <div className="mb-4">
            <p className="text-xs font-semibold text-muted-foreground px-3 mb-3 tracking-wider">TRACKING</p>
            <Link to="/pet-tracking">
              <Button 
                variant={isActive('/pet-tracking') ? 'secondary' : 'ghost'} 
                className={`w-full justify-start transition-all duration-200 ${
                  isActive('/pet-tracking') 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                    : 'hover:bg-muted hover:translate-x-1'
                }`}
                data-testid="nav-pet-tracking"
              >
                <Activity className="mr-3" size={20} />
                Pet Status
              </Button>
            </Link>
          </div>

          <div className="mb-4">
            <p className="text-xs font-semibold text-muted-foreground px-3 mb-3 tracking-wider">REWARDS</p>
            <Link to="/redeem-points">
              <Button 
                variant={isActive('/redeem-points') ? 'secondary' : 'ghost'} 
                className={`w-full justify-start transition-all duration-200 ${
                  isActive('/redeem-points') 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                    : 'hover:bg-muted hover:translate-x-1'
                }`}
                data-testid="nav-redeem-points"
              >
                <Gift className="mr-3" size={20} />
                Redeem Points
              </Button>
            </Link>
            <Link to="/redemption-history">
              <Button 
                variant={isActive('/redemption-history') ? 'secondary' : 'ghost'} 
                className={`w-full justify-start transition-all duration-200 ${
                  isActive('/redemption-history') 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                    : 'hover:bg-muted hover:translate-x-1'
                }`}
                data-testid="nav-redemption-history"
              >
                <History className="mr-3" size={20} />
                Redemption History
              </Button>
            </Link>
          </div>

          <div className="mb-4">
            <p className="text-xs font-semibold text-muted-foreground px-3 mb-3 tracking-wider">MATERIALS</p>
            <Link to="/material-request">
              <Button 
                variant={isActive('/material-request') ? 'secondary' : 'ghost'} 
                className={`w-full justify-start transition-all duration-200 ${
                  isActive('/material-request') 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                    : 'hover:bg-muted hover:translate-x-1'
                }`}
                data-testid="nav-material-request"
              >
                <FileText className="mr-3" size={20} />
                Request Materials
              </Button>
            </Link>
            <Link to="/material-history">
              <Button 
                variant={isActive('/material-history') ? 'secondary' : 'ghost'} 
                className={`w-full justify-start transition-all duration-200 ${
                  isActive('/material-history') 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                    : 'hover:bg-muted hover:translate-x-1'
                }`}
                data-testid="nav-material-history"
              >
                <History className="mr-3" size={20} />
                Material History
              </Button>
            </Link>
          </div>

          <div className="mb-4">
            <p className="text-xs font-semibold text-muted-foreground px-3 mb-3 tracking-wider">SUPPORT</p>
            <Link to="/feedback">
              <Button 
                variant={isActive('/feedback') ? 'secondary' : 'ghost'} 
                className={`w-full justify-start transition-all duration-200 ${
                  isActive('/feedback') 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                    : 'hover:bg-muted hover:translate-x-1'
                }`}
                data-testid="nav-feedback"
              >
                <MessageSquare className="mr-3" size={20} />
                Feedback
              </Button>
            </Link>
          </div>

          {/* Price Guide — all clinics */}
          <div className="mb-4">
            <p className="text-xs font-semibold text-muted-foreground px-3 mb-3 tracking-wider">PRICING</p>
            <Link to="/price-guide">
              <Button 
                variant={isActive('/price-guide') ? 'secondary' : 'ghost'} 
                className={`w-full justify-start transition-all duration-200 ${
                  isActive('/price-guide') 
                    ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                    : 'hover:bg-muted hover:translate-x-1'
                }`}
                data-testid="nav-price-guide"
              >
                <DollarSign className="mr-3" size={20} />
                Price Guide
              </Button>
            </Link>
          </div>
        </nav>

        <div className="p-4 border-t bg-gradient-to-t from-[hsl(190_60%_98%)] to-white space-y-2">
          <div className="mb-3 px-3">
            <p className="text-sm font-medium">{clinic?.clinic_name}</p>
            <p className="text-xs text-muted-foreground">{clinic?.email}</p>
          </div>
          <Link to="/profile">
            <Button 
              variant={isActive('/profile') ? 'secondary' : 'ghost'} 
              className={`w-full justify-start transition-all duration-200 ${
                isActive('/profile') 
                  ? 'bg-primary/10 text-primary border-l-4 border-primary font-semibold' 
                  : 'hover:bg-muted hover:translate-x-1'
              }`}
              data-testid="nav-profile"
            >
              <User className="mr-3" size={20} />
              Profile & Settings
            </Button>
          </Link>
          <Button 
            variant="ghost" 
            className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleLogout}
            data-testid="logout-button"
          >
            <LogOut className="mr-2" size={18} />
            Logout
          </Button>
        </div>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden sticky top-0 z-30 bg-white border-b shadow-sm">
        <div className="flex items-center justify-between p-3">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img 
              src={logoUrl} 
              alt="Clinic Logo" 
              className="w-32 h-auto object-contain"
              style={{ mixBlendMode: 'multiply' }}
              onError={(e) => {
                e.target.src = DEFAULT_LOGO_URL;
              }}
            />
            <div>
              <h2 className="font-semibold text-sm">{clinic?.clinic_name || 'Veterinary Clinic'}</h2>
              <p className="text-[10px] text-muted-foreground tracking-wider">VET PORTAL</p>
            </div>
          </Link>
          <Button variant="ghost" size="sm" onClick={handleLogout}>
            <LogOut size={18} />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:ml-64 min-h-screen overflow-x-hidden">
        <div className="p-4 md:p-8 lg:p-10 pb-24 md:pb-8 max-w-full">
          {children}
        </div>
      </div>

      {/* Mobile Bottom Nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t shadow-lg z-50" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="grid grid-cols-6 p-3 gap-0.5">
          <Link to="/dashboard" className="flex-1">
            <Button variant={isActive('/dashboard') ? 'secondary' : 'ghost'} className="w-full flex flex-col h-16 px-0.5" size="sm">
              <LayoutDashboard size={18} />
              <span className="text-[10px] mt-1 font-medium">Home</span>
            </Button>
          </Link>
          <Link to="/redeem-points" className="flex-1">
            <Button variant={isActive('/redeem-points') ? 'secondary' : 'ghost'} className="w-full flex flex-col h-16 px-0.5" size="sm">
              <Gift size={18} />
              <span className="text-[10px] mt-1 font-medium">Redeem</span>
            </Button>
          </Link>
          <Link to="/pet-tracking" className="flex-1">
            <Button variant={isActive('/pet-tracking') ? 'secondary' : 'ghost'} className="w-full flex flex-col h-16 px-0.5" size="sm">
              <Activity size={18} />
              <span className="text-[10px] mt-1 font-medium">Tracking</span>
            </Button>
          </Link>
          <Link to="/requests/new" className="flex-1">
            <Button variant={isActive('/requests/new') ? 'secondary' : 'ghost'} className="w-full flex flex-col h-16 px-0.5" size="sm">
              <FileText size={18} />
              <span className="text-[10px] mt-1 font-medium">New</span>
            </Button>
          </Link>
          <Link to="/redemption-history" className="flex-1">
            <Button variant={isActive('/redemption-history') ? 'secondary' : 'ghost'} className="w-full flex flex-col h-16 px-0.5" size="sm">
              <DollarSign size={18} />
              <span className="text-[10px] mt-1 font-medium">Redeem H.</span>
            </Button>
          </Link>
          <Link to="/price-guide" className="flex-1">
            <Button variant={isActive('/price-guide') ? 'secondary' : 'ghost'} className="w-full flex flex-col h-16 px-0.5" size="sm">
              <DollarSign size={18} />
              <span className="text-[10px] mt-1 font-medium">Prices</span>
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Layout;
